import { useMemo } from 'react';

import { Category, GraphsNode } from '@data';
import { useTranslate } from '@i18n';

export type UseGraphNodesParams = {
    graphCategories: Category[] | undefined;
    graphNodes: GraphsNode[] | undefined;
    usersGraphNodes: Set<string>;
};
export function useGraphNodes({ graphCategories, graphNodes, usersGraphNodes }: UseGraphNodesParams) {
    const allUserGraphUiNodes = useMemo(
        () => graphNodes?.filter((d) => usersGraphNodes.has(d.id)),
        [graphNodes, usersGraphNodes],
    );
    const t = useTranslate();

    const graphNavigationLinks = useMemo(() => {
        const allCategories = new Set(allUserGraphUiNodes?.map((d) => d.categories.graphType));
        return graphCategories
            ?.sort((a, b) => a.displayOrder - b.displayOrder)
            .reduce(
                (acc, category) => {
                    if (allCategories.has(category.id)) {
                        acc[category.id] = t(category.i18n ?? '-');
                    }
                    return acc;
                },
                {} as Record<string, string>,
            );
    }, [graphCategories, allUserGraphUiNodes]);

    const filteredgraphCategories = useMemo(() => {
        const allUsersGraphCategoriesSet = new Set(allUserGraphUiNodes?.map((d) => d.categories.graphType));
        return graphCategories
            ?.filter((d) => allUsersGraphCategoriesSet.has(d.id))
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }, [graphCategories, allUserGraphUiNodes]);

    const graphMap = useMemo(() => {
        return filteredgraphCategories?.reduce(
            (acc, category) => {
                const graphs = allUserGraphUiNodes?.filter((graph) => graph.categories.graphType === category.id);
                if (graphs) {
                    acc[category.id] = graphs;
                    return acc;
                }
                return acc;
            },
            {} as Record<string, GraphsNode[]>,
        );
    }, [filteredgraphCategories, allUserGraphUiNodes]);

    return {
        graphNavigationLinks,
        filteredgraphCategories,
        graphMap,
    };
}
